/**
 * Application Configuration
 * Centralized configuration that can be overridden by environment variables
 */

export interface AppConfig {
  environment: "development" | "staging" | "production";
  security: {
    rateLimiting: {
      enabled: boolean;
      chat: {
        maxRequests: number;
        windowMs: number;
        blockDurationMs: number;
      };
      email: {
        maxRequests: number;
        windowMs: number;
        blockDurationMs: number;
      };
    };
    captcha: {
      enabled: boolean;
      threshold: number;
    };
    inputValidation: {
      enabled: boolean;
      maxMessageLength: number;
      minMessageLength: number;
      maxEmailLength: number;
      enableSpamDetection: boolean;
      enableProfanityFilter: boolean;
    };
    monitoring: {
      enabled: boolean;
      suspiciousThreshold: number;
      alertOnCritical: boolean;
    };
  };
  dialogflow: {
    enabled: boolean;
    fallbackEnabled: boolean;
  };
  features: {
    adminAccess: boolean;
    userRegistration: boolean;
    mapApproval: boolean;
    // Region feature flags - hidden from users until future release
    regionsEnabled: boolean; // Master switch for regions feature
    regionsVisibleToUsers: boolean; // When true, users can see/select regions
    regionsHistoricalDataEnabled: boolean; // When true, historical periods are shown
  };
}

// Default configuration values
const defaultConfig: AppConfig = {
  environment: "development",
  security: {
    rateLimiting: {
      enabled: true,
      chat: {
        maxRequests: 5,
        windowMs: 60000, // 1 minute
        blockDurationMs: 300000, // 5 minutes
      },
      email: {
        maxRequests: 3,
        windowMs: 3600000, // 1 hour
        blockDurationMs: 1800000, // 30 minutes
      },
    },
    captcha: {
      enabled: false, // Disabled - contact is only for logged-in users
      threshold: 0.5,
    },
    inputValidation: {
      enabled: true,
      maxMessageLength: 2000,
      minMessageLength: 1,
      maxEmailLength: 254,
      enableSpamDetection: true,
      enableProfanityFilter: true,
    },
    monitoring: {
      enabled: true,
      suspiciousThreshold: 10,
      alertOnCritical: true,
    },
  },
  dialogflow: {
    enabled: false,
    fallbackEnabled: true,
  },
  features: {
    adminAccess: true,
    userRegistration: true,
    mapApproval: true,
    // Regions feature - disabled for users until future release
    regionsEnabled: true, // Enabled for admin management
    regionsVisibleToUsers: false, // Hidden from regular users
    regionsHistoricalDataEnabled: false, // Historical data not yet ready
  },
};

// Production configuration overrides
const productionConfig: Partial<AppConfig> = {
  environment: "production",
  security: {
    rateLimiting: {
      enabled: true,
      chat: {
        maxRequests: 5,
        windowMs: 60000,
        blockDurationMs: 300000,
      },
      email: {
        maxRequests: 3,
        windowMs: 3600000,
        blockDurationMs: 1800000,
      },
    },
    captcha: {
      enabled: false, // Disabled - contact is only for logged-in users
      threshold: 0.5,
    },
    inputValidation: {
      enabled: true,
      maxMessageLength: 2000,
      minMessageLength: 1,
      maxEmailLength: 254,
      enableSpamDetection: true,
      enableProfanityFilter: true,
    },
    monitoring: {
      enabled: true,
      suspiciousThreshold: 10,
      alertOnCritical: true,
    },
  },
  dialogflow: {
    enabled: false, // Disable by default for security
    fallbackEnabled: true,
  },
  features: {
    adminAccess: false, // Disable admin access by default
    userRegistration: true,
    mapApproval: true,
    // Regions feature - disabled for users until future release
    regionsEnabled: true,
    regionsVisibleToUsers: false, // Hidden from regular users
    regionsHistoricalDataEnabled: false,
  },
};

// Development configuration overrides
const developmentConfig: Partial<AppConfig> = {
  environment: "development",
  security: {
    rateLimiting: {
      enabled: true,
      chat: {
        maxRequests: 20, // More lenient in development
        windowMs: 60000,
        blockDurationMs: 60000, // Shorter block in development
      },
      email: {
        maxRequests: 3,
        windowMs: 3600000,
        blockDurationMs: 1800000,
      },
    },
    captcha: {
      enabled: false, // Disable CAPTCHA in development
      threshold: 0.5,
    },
    inputValidation: {
      enabled: true,
      maxMessageLength: 2000,
      minMessageLength: 1,
      maxEmailLength: 254,
      enableSpamDetection: true,
      enableProfanityFilter: true,
    },
    monitoring: {
      enabled: true,
      suspiciousThreshold: 10,
      alertOnCritical: true,
    },
  },
  dialogflow: {
    enabled: false, // Use fallback in development
    fallbackEnabled: true,
  },
  features: {
    adminAccess: true,
    userRegistration: true,
    mapApproval: true,
    // Regions feature - enabled for admin testing in development
    regionsEnabled: true,
    regionsVisibleToUsers: false, // Still hidden from users
    regionsHistoricalDataEnabled: true, // Enable for testing
  },
};

/**
 * Get the current environment
 */
const getEnvironment = (): "development" | "staging" | "production" => {
  const env = process.env.NODE_ENV || "development";
  if (env === "production") return "production";
  if (env === "staging") return "staging";
  return "development";
};

/**
 * Deep merge configuration objects
 */
const deepMerge = <T>(target: T, source: Partial<T>): T => {
  const result = { ...target };

  for (const key in source) {
    if (source[key] !== undefined) {
      if (
        typeof source[key] === "object" &&
        source[key] !== null &&
        !Array.isArray(source[key])
      ) {
        result[key] = deepMerge(result[key], source[key] as any);
      } else {
        result[key] = source[key] as any;
      }
    }
  }

  return result;
};

/**
 * Get application configuration
 */
export const getAppConfig = (): AppConfig => {
  const environment = getEnvironment();

  // Start with default config
  let config = { ...defaultConfig };

  // Apply environment-specific overrides
  if (environment === "production") {
    config = deepMerge(config, productionConfig);
  } else if (environment === "development") {
    config = deepMerge(config, developmentConfig);
  }

  // Apply environment variable overrides for sensitive values
  // CAPTCHA is disabled - contact is only for logged-in users

  if (
    process.env.REACT_APP_DIALOGFLOW_PROJECT_ID &&
    process.env.REACT_APP_DIALOGFLOW_ACCESS_TOKEN
  ) {
    config.dialogflow.enabled = true;
  }

  // Override with explicit environment variables if set
  if (process.env.REACT_APP_RATE_LIMIT_CHAT_MAX) {
    config.security.rateLimiting.chat.maxRequests = parseInt(
      process.env.REACT_APP_RATE_LIMIT_CHAT_MAX
    );
  }

  if (process.env.REACT_APP_RATE_LIMIT_CHAT_WINDOW) {
    config.security.rateLimiting.chat.windowMs = parseInt(
      process.env.REACT_APP_RATE_LIMIT_CHAT_WINDOW
    );
  }

  if (process.env.REACT_APP_RATE_LIMIT_CHAT_BLOCK) {
    config.security.rateLimiting.chat.blockDurationMs = parseInt(
      process.env.REACT_APP_RATE_LIMIT_CHAT_BLOCK
    );
  }

  if (process.env.REACT_APP_RECAPTCHA_THRESHOLD) {
    config.security.captcha.threshold = parseFloat(
      process.env.REACT_APP_RECAPTCHA_THRESHOLD
    );
  }

  if (process.env.REACT_APP_MAX_MESSAGE_LENGTH) {
    config.security.inputValidation.maxMessageLength = parseInt(
      process.env.REACT_APP_MAX_MESSAGE_LENGTH
    );
  }

  if (process.env.REACT_APP_SUSPICIOUS_THRESHOLD) {
    config.security.monitoring.suspiciousThreshold = parseInt(
      process.env.REACT_APP_SUSPICIOUS_THRESHOLD
    );
  }

  return config;
};

// Export the configuration
export const appConfig = getAppConfig();

// Validation function
export const validateAppConfig = (config: AppConfig): string[] => {
  const errors: string[] = [];

  if (config.security.rateLimiting.chat.maxRequests <= 0) {
    errors.push("Chat rate limit max requests must be positive");
  }

  if (config.security.rateLimiting.chat.windowMs <= 0) {
    errors.push("Chat rate limit window must be positive");
  }

  if (config.security.rateLimiting.chat.blockDurationMs <= 0) {
    errors.push("Chat rate limit block duration must be positive");
  }

  if (config.security.inputValidation.maxMessageLength <= 0) {
    errors.push("Max message length must be positive");
  }

  if (config.security.inputValidation.minMessageLength <= 0) {
    errors.push("Min message length must be positive");
  }

  if (config.security.inputValidation.maxEmailLength <= 0) {
    errors.push("Max email length must be positive");
  }

  // CAPTCHA validation removed - disabled since contact is only for logged-in users

  if (config.security.monitoring.suspiciousThreshold <= 0) {
    errors.push("Suspicious threshold must be positive");
  }

  return errors;
};

// Validate configuration on import
const configErrors = validateAppConfig(appConfig);
if (configErrors.length > 0) {
  console.error("App configuration errors:", configErrors);
  if (appConfig.environment === "production") {
    throw new Error(`Invalid app configuration: ${configErrors.join(", ")}`);
  }
}

export default appConfig;
